import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { Observable, of, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { QuestionBase } from "../../../shared/components/dynamic-form/question-base";
import { TextboxQuestion } from "../../../shared/components/dynamic-form/question-textbox";
import { pattern } from "../../../shared/constants/patterns";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { AnyObject } from "../../../shared/types/app.type";
import { arePasswordsEquals } from "../../../shared/validators/validators";
import { ChangePassword, User } from "./user.type";

@Injectable({
  providedIn: "root",
})
export class UserService {
  basePath = `${environment.serverUrl}/users`;
  basePathCsvQueue = `${this.basePath}/queue-csv`;

  constructor(private http: HttpClient) {}

  createUser(data: AnyObject): Observable<User> {
    return this.http.post<User>(this.basePath, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  registerUser(data): Observable<User> {
    return this.http.post<User>(`${this.basePath}/register`, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  restoreUser(id: string): Observable<User> {
    return this.http.post<User>(`${this.basePath}/${id}/restore`, {}).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getUsers(options?: QueryParams<User>): Observable<PaginatedResponse<User>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<PaginatedResponse<User>>(this.basePath, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getUsersPost(options?: QueryParams<User>): Observable<PaginatedResponse<User>> {
    const body = { ...DEFAULT_QUERY_PARAMS, ...options };
    return this.http.post<PaginatedResponse<User>>(`${this.basePath}/get-many`, body).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  getUser(userId: string, options?: QueryParams<User>): Observable<User> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http.get<User>(`${this.basePath}/${userId}`, { params }).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateUserInformation(userId: string, data): Observable<User> {
    return this.http.put<User>(`${this.basePath}/${userId}/information`, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }
  updateUserPermissions(userId: string, data): Observable<User> {
    return this.http.put<User>(`${this.basePath}/${userId}/permissions`, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }
  updateUserNotifications(userId: string, data): Observable<User> {
    return this.http.put<User>(`${this.basePath}/${userId}/notifications`, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  updateUserProfile(userId: string, data): Observable<User> {
    return this.http.put<User>(`${this.basePath}/profile/${userId}`, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  activateUser(userId: string, data): Observable<User> {
    return this.http.put<User>(`${this.basePath}/${userId}/activate`, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  changePassword(data: any): Observable<User> {
    return this.http.post<User>(`${this.basePath}/change-password`, data).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  changePasswordAdmin(password: Pick<ChangePassword, "password">, user: User): Observable<User> {
    return this.http.put<User>(`${this.basePath}/admin/change-password/${user.id}`, password).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  confirmUser(user: User): Observable<void> {
    return this.http.put<User>(`${this.basePath}/confirm/${user.id}`, {}).pipe(
      tap((_data: User) => console.log()),
      catchError(this.handleError<any>("")),
    );
  }

  linkCompanies(id: string, data): Observable<void> {
    return this.http.post<void>(`${this.basePath}/${id}/companies`, data).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("linkcompanies")),
    );
  }

  linkPartners(id: string, data): Observable<void> {
    return this.http.post<void>(`${this.basePath}/${id}/partners`, data).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>("linkPartners")),
    );
  }

  getUsersQueue(options?: QueryParams<User>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(
      tap((_data) => console.log()),
      catchError(this.handleError<any>()),
    );
  }

  generateQuestionsChangePassword(options?): Observable<QuestionBase<string | boolean>[]> {
    const questions: QuestionBase<string | boolean>[] = [
      new TextboxQuestion({
        value: options?.password,
        key: "password",
        label: "Password",
        required: true,
        type: "password",
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Password <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Password is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Password is <strong>too long</strong>",
          },
        ],
        order: 1,
      }),
      new TextboxQuestion({
        value: options?.passwordRepeat,
        key: "passwordRepeat",
        label: "Repeat password",
        required: true,
        type: "password",
        errors: [
          {
            key: "passwordRepeat",
            validator: arePasswordsEquals(),
            message: "<strong>Passwords</strong> do not match",
          },
        ],
        order: 2,
      }),
    ];
    return of(questions.filter((fields) => fields).sort((a, b) => a.order - b.order));
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return throwError(error);
    };
  }
}
